import { useEffect, useState } from 'react'
import { fetchStats } from '@/lib/api'
import type { Stats } from '@/lib/api'
import { Loader2, Library, FileText, Link, AlertCircle } from 'lucide-react'

export function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchStats()
      .then(setStats)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        {error || 'Failed to load stats'}
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <h1 className="text-2xl font-light mb-8">Dashboard</h1>

      {/* Overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <StatCard
          title="Total Cards"
          value={stats.total_cards}
          icon={<Library className="h-5 w-5" />}
        />
        <StatCard
          title="Total Sources"
          value={stats.total_sources}
          icon={<FileText className="h-5 w-5" />}
        />
        <StatCard
          title="Total Edges"
          value={stats.total_edges}
          icon={<Link className="h-5 w-5" />}
        />
        <StatCard
          title="Pending Review"
          value={stats.review_pending}
          icon={<AlertCircle className="h-5 w-5" />}
        />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* By Domain */}
        <div className="p-6 rounded-lg border border-border bg-card">
          <h2 className="text-lg font-light mb-6">Cards by Domain</h2>
          <div className="space-y-3">
            {Object.entries(stats.cards_by_domain).map(([domain, count]) => (
              <div key={domain} className="flex items-center justify-between">
                <span className="text-sm font-medium">{domain}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{
                        width: `${
                          stats.total_cards > 0
                            ? (count / stats.total_cards) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8 text-right">
                    {count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By Phase */}
        <div className="p-6 rounded-lg border border-border bg-card">
          <h2 className="text-lg font-light mb-6">Cards by Phase</h2>
          <div className="space-y-3">
            {Object.entries(stats.cards_by_phase).map(([phase, count]) => (
              <div key={phase} className="flex items-center justify-between">
                <span className="text-sm font-light capitalize">{phase}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-foreground rounded-full"
                      style={{
                        width: `${
                          stats.total_cards > 0
                            ? (count / stats.total_cards) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8 text-right">
                    {count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By Type */}
        <div className="p-6 rounded-lg border border-border bg-card">
          <h2 className="text-lg font-light mb-6">Cards by Type</h2>
          <div className="space-y-3 max-h-64 overflow-auto">
            {Object.entries(stats.cards_by_type)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm font-light">
                    {type.replace('_', ' ')}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-foreground rounded-full"
                        style={{
                          width: `${
                            stats.total_cards > 0
                              ? (count / stats.total_cards) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-8 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  icon,
}: {
  title: string
  value: number
  icon: React.ReactNode
}) {
  return (
    <div className="p-6 rounded-lg border border-border bg-card">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-muted text-foreground">{icon}</div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-light">{value}</p>
        </div>
      </div>
    </div>
  )
}
